import React, { useState, useEffect, useRef } from 'react';
import { Box, Loader, CheckCircle, XCircle, RotateCcw, Eye, Sparkles, AlertCircle, PartyPopper, Camera, Image, Upload } from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { app } from '../firebase/config';
// heic2any remplacé par Cloud Function convertHeicToJpeg (meilleur support iPhone)

// Son de notification (base64 court)
const playSuccessSound = () => {
  try {
    const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleVIpP5Xd6sV3LxYzmN3wzYM4FjGb3/XMgTYYM5rd9M2EORI0muD0zYM4FjOa3/XMgTYYM5rd9M2EORI0muD0zYM4FjOa3/XMgTYYM5rd9M2EORI0muD0zYM4FjOa3/XMgTYYM5rd9M2EORI0muD0zQ==');
    audio.volume = 0.5;
    audio.play().catch(() => {});
  } catch (e) {}
};

// Toast notification component
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 5000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`fixed top-4 right-4 z-50 animate-slide-in-right flex items-center gap-3 px-5 py-4 rounded-xl shadow-2xl ${
      type === 'success' ? 'bg-gradient-to-r from-green-500 to-emerald-500' : 'bg-gradient-to-r from-red-500 to-pink-500'
    } text-white`}>
      {type === 'success' ? <PartyPopper className="w-6 h-6" /> : <AlertCircle className="w-6 h-6" />}
      <span className="font-medium">{message}</span>
      <button onClick={onClose} className="ml-2 hover:opacity-70">✕</button>
    </div>
  );
};

/**
 * Section AR/3D pour la gestion des produits
 * - Génère un modèle 3D à partir de l'image produit
 * - Affiche la progression en temps réel
 * - Preview 3D avec model-viewer
 * - Validation/Rejet/Régénération
 */
export const ProductARSection = ({ product, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null); // { message, type }

  // État local pour le polling (indépendant du refresh parent)
  const [localStatus, setLocalStatus] = useState(null);
  const [localProgress, setLocalProgress] = useState(0);
  const [localTaskId, setLocalTaskId] = useState(null);

  // Images de référence pour génération 3D (multi-photos)
  const [useReferenceImage, setUseReferenceImage] = useState(false);
  const [referenceImages, setReferenceImages] = useState(product?.ar_reference_images || []);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [uploadingRef, setUploadingRef] = useState(false);
  const fileInputRef = useRef(null);

  // URL de l'image sélectionnée pour la génération
  const referenceImageUrl = referenceImages[selectedImageIndex] || null;

  // Scale AR (0.1 à 1.0)
  const [arScale, setArScale] = useState(product?.ar_scale || 0.15);

  const pollingRef = useRef(null);
  const functions = getFunctions(app, 'europe-west1');
  const storage = getStorage(app);

  // Lecture des champs AR du produit (ou état local si en cours)
  const arStatus = localStatus || product?.ar_status || 'none';
  const arProgress = localStatus === 'processing' ? localProgress : (product?.ar_progress || 0);
  const arTaskId = localTaskId || product?.ar_task_id;
  const arModelGlb = product?.ar_model_glb;
  const arModelUsdz = product?.ar_model_usdz;
  const arError = product?.ar_error;


  // Nettoyage du polling au démontage
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, []);

  // Charger les images de référence existantes du produit
  useEffect(() => {
    if (product?.ar_reference_images?.length > 0 && referenceImages.length === 0) {
      setReferenceImages(product.ar_reference_images);
    }
  }, [product?.ar_reference_images]);

  // Reprendre le polling si on revient sur un produit en cours
  useEffect(() => {
    if (product?.ar_status === 'processing' && product?.ar_task_id && !pollingRef.current) {
      setLocalStatus('processing');
      setLocalTaskId(product.ar_task_id);
      setLocalProgress(product.ar_progress || 0);
      startPolling(product.ar_task_id);
    }
  }, [product?.ar_status, product?.ar_task_id]);

  const startPolling = (taskId) => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
    }

    console.log('[AR] Démarrage polling pour taskId:', taskId);

    pollingRef.current = setInterval(async () => {
      try {
        const checkStatus = httpsCallable(functions, 'check3DModelStatus');
        const result = await checkStatus({
          productId: product.id,
          taskId: taskId
        });

        console.log('[AR] Polling result:', result.data);

        // Mise à jour du progress local
        setLocalProgress(result.data.progress || 0);

        // Si terminé (succès ou erreur)
        if (result.data.status !== 'processing') {
          console.log('[AR] Génération terminée:', result.data.status);
          clearInterval(pollingRef.current);
          pollingRef.current = null;

          // Notification de fin
          if (result.data.status === 'pending_validation' || result.data.status === 'ready') {
            playSuccessSound();
            setToast({
              message: '🎉 Modèle 3D généré avec succès ! Prêt pour validation.',
              type: 'success'
            });
          } else if (result.data.status === 'error') {
            setToast({
              message: '❌ Échec de la génération 3D. Réessayez.',
              type: 'error'
            });
          }

          // Reset état local et rafraîchir depuis parent
          setLocalStatus(null);
          setLocalTaskId(null);
          setLocalProgress(0);
          onUpdate?.();
        }
      } catch (err) {
        console.error('[AR] Erreur polling:', err);
      }
    }, 3000);
  };

  // Vérifier les dimensions d'une image
  const checkImageDimensions = (file) => {
    return new Promise((resolve) => {
      const img = new window.Image();
      img.onload = () => {
        URL.revokeObjectURL(img.src);
        resolve({ width: img.width, height: img.height });
      };
      img.onerror = () => resolve({ width: 0, height: 0 });
      img.src = URL.createObjectURL(file);
    });
  };

  // Convertir HEIC/HEIF en JPEG via Cloud Function (meilleur support iPhone)
  const convertHeicToJpeg = async (file) => {
    const isHeic = file.name.toLowerCase().endsWith('.heic') || file.name.toLowerCase().endsWith('.heif');
    if (!isHeic) return file;

    try {
      console.log(`[AR] 🔄 Conversion HEIC → JPEG (Cloud Function):`, {
        name: file.name,
        size: file.size,
        type: file.type
      });

      // Vérifier la taille (max 20MB pour éviter timeout)
      if (file.size > 20 * 1024 * 1024) {
        throw new Error('Fichier trop volumineux (max 20MB)');
      }

      // Lire le fichier en base64
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      // Appeler la Cloud Function
      const convertHeic = httpsCallable(functions, 'convertHeicToJpeg');
      const result = await convertHeic({ imageBase64: base64, fileName: file.name });

      if (!result.data.success) {
        throw new Error('Conversion échouée');
      }

      // Convertir le résultat base64 en File
      const response = await fetch(result.data.jpegBase64);
      const blob = await response.blob();
      const jpegFile = new File([blob], file.name.replace(/\.heic$/i, '.jpg').replace(/\.heif$/i, '.jpg'), { type: 'image/jpeg' });

      console.log(`[AR] ✅ Conversion réussie: ${jpegFile.size} bytes`);
      return jpegFile;
    } catch (err) {
      console.error('[AR] ❌ Erreur conversion HEIC:', err);
      setError(`Erreur HEIC: ${err.message || 'Erreur de conversion'}`);
      throw err;
    }
  };

  // Amélioration automatique de l'image (gratuit, client-side)
  const enhanceImage = (file) => {
    return new Promise((resolve) => {
      const img = new window.Image();
      img.onload = () => {
        // Dimensions cibles (min 1040px)
        const minSize = 1040;
        let width = img.width;
        let height = img.height;
        const originalWidth = width;
        const originalHeight = height;
        let wasUpscaled = false;

        // Upscale si trop petit
        if (width < minSize || height < minSize) {
          const scale = Math.max(minSize / width, minSize / height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
          wasUpscaled = true;
        }

        // Créer canvas
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');

        // Dessiner image redimensionnée
        ctx.drawImage(img, 0, 0, width, height);

        // Améliorer contraste et netteté
        ctx.filter = 'contrast(1.1) saturate(1.05)';
        ctx.drawImage(canvas, 0, 0);
        ctx.filter = 'none';

        // Log debug si upscale
        if (wasUpscaled) {
          console.log(`[AR] 🔍 Image upscalée: ${originalWidth}x${originalHeight} → ${width}x${height}`);
        }

        // Convertir en blob
        canvas.toBlob((blob) => {
          const enhancedFile = new File([blob], file.name, { type: 'image/jpeg' });
          URL.revokeObjectURL(img.src);
          resolve(enhancedFile);
        }, 'image/jpeg', 0.92);
      };
      img.onerror = () => resolve(file); // Fallback: fichier original
      img.src = URL.createObjectURL(file);
    });
  };

  // Upload d'une ou plusieurs images de référence
  const handleReferenceUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    // Vérifier les types et taille (max 10MB par fichier)
    const validFiles = Array.from(files).filter(file => {
      console.log(`[AR] 📁 Fichier: ${file.name}, type: "${file.type}", size: ${file.size}`);
      // Accepter les images classiques + HEIC/HEIF (photos iPhone)
      const isImage = file.type.startsWith('image/');
      const isHeic = file.name.toLowerCase().endsWith('.heic') || file.name.toLowerCase().endsWith('.heif');
      console.log(`[AR] → isImage: ${isImage}, isHeic: ${isHeic}`);
      if (!isImage && !isHeic) {
        console.log('[AR] ❌ Fichier rejeté: ni image ni HEIC');
        setError('Certains fichiers ne sont pas des images');
        return false;
      }
      if (file.size > 10 * 1024 * 1024) {
        setError('Fichiers trop volumineux (max 10MB)');
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;

    // Limite à 4 images max (grille 2x2)
    if (referenceImages.length + validFiles.length > 4) {
      setError('Maximum 4 images de référence');
      return;
    }

    setUploadingRef(true);
    setError(null);

    try {
      const uploadedUrls = [];

      for (const file of validFiles) {
        // Convertir HEIC si nécessaire (photos iPhone)
        const jpegFile = await convertHeicToJpeg(file);

        // Améliorer l'image (upscale si < 1040px, contraste, netteté)
        const enhancedFile = await enhanceImage(jpegFile);
        console.log(`[AR] ✨ Image améliorée: ${file.name} (${file.size} → ${enhancedFile.size} bytes)`);

        // Upload vers Firebase Storage
        const storageRef = ref(storage, `ar-reference/${product.id}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}.jpg`);
        await uploadBytes(storageRef, enhancedFile);
        const url = await getDownloadURL(storageRef);
        uploadedUrls.push(url);
      }

      const newImages = [...referenceImages, ...uploadedUrls];
      setReferenceImages(newImages);
      setSelectedImageIndex(newImages.length - 1); // Sélectionner la dernière uploadée
      setUseReferenceImage(true);
      console.log('[AR] Images de référence uploadées:', uploadedUrls.length);
    } catch (err) {
      console.error('[AR] Erreur upload:', err);
      setError('Erreur lors de l\'upload des images');
    } finally {
      setUploadingRef(false);
    }
  };

  // Supprimer une image de référence
  const handleRemoveReferenceImage = (index) => {
    const newImages = referenceImages.filter((_, i) => i !== index);
    setReferenceImages(newImages);
    if (selectedImageIndex >= newImages.length) {
      setSelectedImageIndex(Math.max(0, newImages.length - 1));
    }
    if (newImages.length === 0) {
      setUseReferenceImage(false);
    }
  };

  const handleGenerate = async () => {
    const sourceImage = useReferenceImage ? referenceImageUrl : product.image_url;

    if (!sourceImage) {
      setError(useReferenceImage
        ? 'Veuillez d\'abord uploader une image de référence'
        : 'Le produit doit avoir une image pour générer un modèle 3D'
      );
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Utiliser l'image de référence comme source principale si sélectionnée
      const mainImageUrl = useReferenceImage ? referenceImageUrl : product.image_url;

      console.log('[AR] Démarrage génération pour:', product.id, useReferenceImage ? `(avec image de référence #${selectedImageIndex + 1})` : '');
      const generate3DModel = httpsCallable(functions, 'generate3DModel');
      const result = await generate3DModel({
        productId: product.id,
        imageUrl: mainImageUrl,
        referenceImageUrl: useReferenceImage ? referenceImageUrl : null,
        referenceImages: useReferenceImage ? referenceImages : []
      });

      console.log('[AR] Génération démarrée:', result.data);

      // Récupérer le taskId de la réponse
      const taskId = result.data.taskId;

      if (taskId) {
        // Mettre à jour l'état local immédiatement
        setLocalStatus('processing');
        setLocalTaskId(taskId);
        setLocalProgress(0);

        // Démarrer le polling avec le taskId
        startPolling(taskId);
      }
    } catch (err) {
      console.error('[AR] Erreur génération:', err);
      setError(err.message || 'Erreur lors de la génération');
      setLocalStatus(null);
    } finally {
      setLoading(false);
    }
  };

  const handleValidate = async (action) => {
    setLoading(true);
    setError(null);

    try {
      const validate3DModel = httpsCallable(functions, 'validate3DModel');
      await validate3DModel({
        productId: product.id,
        action,
        scale: arScale // Envoyer le scale
      });

      onUpdate?.();

      if (action === 'regenerate') {
        setTimeout(() => handleGenerate(), 500);
      }
    } catch (err) {
      console.error('[AR] Erreur validation:', err);
      setError(err.message || 'Erreur lors de la validation');
    } finally {
      setLoading(false);
    }
  };

  // Sauvegarder le scale séparément
  const handleSaveScale = async () => {
    setLoading(true);
    try {
      const validate3DModel = httpsCallable(functions, 'validate3DModel');
      await validate3DModel({
        productId: product.id,
        action: 'update_scale',
        scale: arScale
      });
      onUpdate?.();
    } catch (err) {
      console.error('[AR] Erreur sauvegarde scale:', err);
      setError(err.message || 'Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  // Rendu selon le statut
  const renderContent = () => {
    switch (arStatus) {
      case 'none':
      case undefined:
        return (
          <div className="py-4 space-y-4">

            {/* ÉTAPE 1 : Upload de vraies photos (recommandé) */}
            <div className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border-2 border-purple-200">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-xs font-bold">1</div>
                <h4 className="font-semibold text-gray-800">Uploadez vos photos du plat</h4>
                <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">Recommandé</span>
              </div>

              <p className="text-sm text-gray-600 mb-4">
                Les vraies photos donnent un rendu 3D beaucoup plus réaliste que les images IA.
              </p>

              {/* Zone d'upload */}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleReferenceUpload}
                accept="image/*,.heic,.heif,image/heic,image/heif"
                multiple
                className="hidden"
              />

              {referenceImages.length === 0 ? (
                <button
                  onClick={() => { setUseReferenceImage(true); fileInputRef.current?.click(); }}
                  disabled={uploadingRef}
                  className="w-full p-6 border-2 border-dashed border-purple-300 rounded-xl hover:border-purple-500 hover:bg-white transition-all group"
                >
                  {uploadingRef ? (
                    <Loader className="w-8 h-8 text-purple-500 mx-auto animate-spin" />
                  ) : (
                    <>
                      <Camera className="w-10 h-10 text-purple-400 mx-auto mb-2 group-hover:text-purple-600 transition-colors" />
                      <p className="text-sm font-medium text-gray-700">Cliquez pour ajouter des photos</p>
                      <p className="text-xs text-gray-400 mt-1">iPhone, Android, appareil photo... (max 4 photos)</p>
                    </>
                  )}
                </button>
              ) : (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-purple-700">
                      📸 {referenceImages.length}/4 photo{referenceImages.length > 1 ? 's' : ''} uploadée{referenceImages.length > 1 ? 's' : ''}
                    </span>
                    {referenceImages.length < 4 && (
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadingRef}
                        className="text-sm text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1"
                      >
                        <Upload className="w-4 h-4" /> Ajouter
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {referenceImages.map((url, idx) => (
                      <div
                        key={idx}
                        className={`relative aspect-square rounded-xl overflow-hidden cursor-pointer border-3 transition-all shadow-sm ${
                          selectedImageIndex === idx
                            ? 'border-purple-500 ring-2 ring-purple-300 scale-[1.02]'
                            : 'border-gray-200 hover:border-purple-300'
                        }`}
                        onClick={() => { setSelectedImageIndex(idx); setUseReferenceImage(true); }}
                      >
                        <img
                          src={url}
                          alt={`Photo ${idx + 1}`}
                          className="w-full h-full object-cover"
                        />
                        {selectedImageIndex === idx && useReferenceImage && (
                          <div className="absolute top-2 left-2 bg-purple-600 text-white text-xs px-2 py-1 rounded-full font-medium flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" /> Sélectionnée
                          </div>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveReferenceImage(idx);
                          }}
                          className="absolute top-2 right-2 w-6 h-6 bg-black/50 hover:bg-red-500 rounded-full flex items-center justify-center transition-colors"
                        >
                          <XCircle className="w-4 h-4 text-white" />
                        </button>
                      </div>
                    ))}
                  </div>
                  {referenceImages.length > 1 && (
                    <p className="text-xs text-purple-600 mt-2 text-center">
                      👆 Cliquez sur la photo à utiliser pour la génération
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* CONSEILS PHOTO - Accordéon */}
            <details className="bg-blue-50 rounded-xl border border-blue-200 overflow-hidden">
              <summary className="p-3 cursor-pointer flex items-center gap-2 text-sm font-medium text-blue-700 hover:bg-blue-100 transition-colors">
                <span>💡</span> Conseils pour une bonne photo 3D
                <span className="ml-auto text-blue-400">▼</span>
              </summary>
              <div className="px-4 pb-4 grid grid-cols-2 gap-3">
                <div className="flex items-start gap-2">
                  <span className="text-lg">☀️</span>
                  <div>
                    <p className="text-xs font-medium text-gray-700">Bonne lumière</p>
                    <p className="text-xs text-gray-500">Naturelle de préférence</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-lg">🎯</span>
                  <div>
                    <p className="text-xs font-medium text-gray-700">Centré</p>
                    <p className="text-xs text-gray-500">Plat au milieu de l'image</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-lg">📐</span>
                  <div>
                    <p className="text-xs font-medium text-gray-700">Vue de face</p>
                    <p className="text-xs text-gray-500">Ou légèrement incliné</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-lg">🖼️</span>
                  <div>
                    <p className="text-xs font-medium text-gray-700">Fond uni</p>
                    <p className="text-xs text-gray-500">Blanc ou clair idéal</p>
                  </div>
                </div>
              </div>
            </details>

            {/* OPTION ALTERNATIVE : Utiliser l'image IA */}
            {product.image_url && (
              <div className={`p-3 rounded-xl border transition-all ${
                !useReferenceImage && referenceImages.length === 0
                  ? 'bg-orange-50 border-orange-200'
                  : 'bg-gray-50 border-gray-200'
              }`}>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!useReferenceImage || referenceImages.length === 0}
                    onChange={() => setUseReferenceImage(referenceImages.length > 0)}
                    className="mt-1 w-4 h-4 accent-orange-500"
                    disabled={referenceImages.length === 0}
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Image className="w-4 h-4 text-gray-500" />
                      <span className="text-sm font-medium text-gray-700">Utiliser l'image produit actuelle</span>
                    </div>
                    <p className="text-xs text-orange-600 mt-1">
                      ⚠️ Les images IA donnent un rendu 3D moins réaliste
                    </p>
                  </div>
                  {product.image_url && (
                    <img src={product.image_url} alt="Image produit" className="w-12 h-12 rounded-lg object-cover border" />
                  )}
                </label>
              </div>
            )}

            {/* BOUTON GÉNÉRER */}
            <div className="pt-2">
              <button
                onClick={handleGenerate}
                disabled={loading || (!product.image_url && referenceImages.length === 0)}
                className="w-full py-4 bg-gradient-to-r from-purple-600 to-pink-500 text-white rounded-xl hover:from-purple-700 hover:to-pink-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg font-semibold text-base flex items-center justify-center gap-3"
              >
                {loading ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    Préparation...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5" />
                    Générer le modèle 3D
                  </>
                )}
              </button>

              {referenceImages.length === 0 && !product.image_url && (
                <p className="text-center text-orange-600 text-xs mt-2">
                  ⚠️ Uploadez au moins une photo pour continuer
                </p>
              )}

              {referenceImages.length > 0 && useReferenceImage && (
                <p className="text-center text-purple-600 text-xs mt-2">
                  ✨ Photo {selectedImageIndex + 1} sera utilisée pour la génération
                </p>
              )}
            </div>
          </div>
        );

      case 'processing':
        return (
          <div className="text-center py-6">
            <div className="relative w-24 h-24 mx-auto mb-4">
              <svg className="w-24 h-24 transform -rotate-90">
                <circle
                  cx="48"
                  cy="48"
                  r="40"
                  stroke="#E5E7EB"
                  strokeWidth="8"
                  fill="none"
                />
                <circle
                  cx="48"
                  cy="48"
                  r="40"
                  stroke="url(#gradient)"
                  strokeWidth="8"
                  fill="none"
                  strokeLinecap="round"
                  strokeDasharray={251.2}
                  strokeDashoffset={251.2 - (251.2 * arProgress) / 100}
                  className="transition-all duration-500"
                />
                <defs>
                  <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#9333EA" />
                    <stop offset="100%" stopColor="#EC4899" />
                  </linearGradient>
                </defs>
              </svg>
              <span className="absolute inset-0 flex items-center justify-center text-purple-600 font-bold text-xl">
                {arProgress}%
              </span>
            </div>
            <p className="text-gray-700 font-medium mb-2">Génération en cours...</p>
            <div className="w-full bg-gray-200 rounded-full h-2 mb-3 overflow-hidden">
              <div
                className="bg-gradient-to-r from-purple-500 to-pink-500 h-full rounded-full transition-all duration-500 ease-out"
                style={{ width: `${arProgress}%` }}
              />
            </div>
            <p className="text-gray-500 text-sm">
              {arProgress < 20 && '🔍 Analyse de l\'image...'}
              {arProgress >= 20 && arProgress < 50 && '🧊 Création du modèle 3D...'}
              {arProgress >= 50 && arProgress < 80 && '🎨 Application des textures...'}
              {arProgress >= 80 && '✨ Finalisation...'}
            </p>
            <p className="text-gray-400 text-xs mt-2">
              Cela peut prendre 1-2 minutes
            </p>
          </div>
        );

      case 'pending_validation':
        return (
          <div className="py-4">
            <div className="flex items-center gap-2 mb-4">
              <Eye className="w-5 h-5 text-orange-500" />
              <span className="font-medium text-gray-700">Aperçu du modèle 3D</span>
              <span className="text-xs bg-orange-100 text-orange-600 px-2 py-0.5 rounded-full">
                En attente de validation
              </span>
            </div>

            {arModelGlb && (
              <div className="bg-gray-100 rounded-xl overflow-hidden mb-4 border border-gray-200 flex items-center justify-center" style={{ height: 280 }}>
                <model-viewer
                  src={arModelGlb}
                  ios-src={arModelUsdz}
                  alt={`Modèle 3D de ${product.name}`}
                  camera-controls
                  auto-rotate
                  shadow-intensity="1"
                  exposure="0.8"
                  scale={`${arScale} ${arScale} ${arScale}`}
                  style={{ width: '100%', height: '100%' }}
                >
                  <div slot="poster" className="flex items-center justify-center h-full text-gray-400 text-sm">Chargement 3D...</div>
                  <div slot="error" className="flex items-center justify-center h-full text-gray-400 text-sm">Aperçu 3D non disponible</div>
                </model-viewer>
              </div>
            )}

            {/* Slider de taille AR */}
            <div className="mb-4 p-4 bg-white rounded-xl border border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Taille en AR</span>
                <span className="text-sm font-bold text-purple-600">{Math.round(arScale * 100)}%</span>
              </div>
              <input
                type="range"
                min="0.05"
                max="1"
                step="0.05"
                value={arScale}
                onChange={(e) => setArScale(parseFloat(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>5%</span>
                <span>50%</span>
                <span>100%</span>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                💡 Pour un plat, essayez 20-30%. Ajustez selon la taille réelle du produit.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => handleValidate('validate')}
                disabled={loading}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-green-500 text-white rounded-xl hover:bg-green-600 disabled:opacity-50 transition-colors font-medium"
              >
                {loading ? <Loader className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                Valider
              </button>
              <button
                onClick={() => handleValidate('regenerate')}
                disabled={loading}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-orange-500 text-white rounded-xl hover:bg-orange-600 disabled:opacity-50 transition-colors font-medium"
              >
                <RotateCcw className="w-4 h-4" />
                Régénérer
              </button>
              <button
                onClick={() => handleValidate('reject')}
                disabled={loading}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-red-500 text-white rounded-xl hover:bg-red-600 disabled:opacity-50 transition-colors font-medium"
              >
                <XCircle className="w-4 h-4" />
                Annuler
              </button>
            </div>
          </div>
        );

      case 'ready':
        return (
          <div className="py-4">
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span className="font-medium text-gray-700">AR activée</span>
              <span className="text-xs bg-green-100 text-green-600 px-2 py-0.5 rounded-full">
                Visible dans l'app
              </span>
            </div>

            {arModelGlb && (
              <div className="bg-gray-100 rounded-xl overflow-hidden mb-4 border border-gray-200 flex items-center justify-center" style={{ height: 200 }}>
                <model-viewer
                  src={arModelGlb}
                  ios-src={arModelUsdz}
                  alt={`Modèle 3D de ${product.name}`}
                  camera-controls
                  auto-rotate
                  shadow-intensity="1"
                  scale={`${arScale} ${arScale} ${arScale}`}
                  style={{ width: '100%', height: '100%' }}
                >
                  <div slot="poster" className="flex items-center justify-center h-full text-gray-400 text-sm">Chargement 3D...</div>
                  <div slot="error" className="flex items-center justify-center h-full text-gray-400 text-sm">Aperçu 3D non disponible</div>
                </model-viewer>
              </div>
            )}

            {/* Slider de taille AR */}
            <div className="mb-4 p-4 bg-white rounded-xl border border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Taille en AR</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-purple-600">{Math.round(arScale * 100)}%</span>
                  {arScale !== (product?.ar_scale || 0.3) && (
                    <button
                      onClick={handleSaveScale}
                      disabled={loading}
                      className="px-2 py-1 text-xs bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                    >
                      {loading ? '...' : 'Sauvegarder'}
                    </button>
                  )}
                </div>
              </div>
              <input
                type="range"
                min="0.05"
                max="1"
                step="0.05"
                value={arScale}
                onChange={(e) => setArScale(parseFloat(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>5%</span>
                <span>50%</span>
                <span>100%</span>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => handleValidate('regenerate')}
                disabled={loading}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 text-gray-600 rounded-xl hover:bg-gray-50 disabled:opacity-50 transition-colors"
              >
                <RotateCcw className="w-4 h-4" />
                Régénérer
              </button>
              <button
                onClick={() => handleValidate('reject')}
                disabled={loading}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 border border-red-300 text-red-600 rounded-xl hover:bg-red-50 disabled:opacity-50 transition-colors"
              >
                <XCircle className="w-4 h-4" />
                Désactiver
              </button>
            </div>
          </div>
        );

      case 'rejected':
        return (
          <div className="text-center py-6">
            <XCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 font-medium mb-2">AR désactivée</p>
            <button
              onClick={handleGenerate}
              disabled={loading}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-purple-600 to-pink-500 text-white rounded-xl hover:from-purple-700 hover:to-pink-600 disabled:opacity-50 transition-all"
            >
              {loading ? <Loader className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
              Réessayer
            </button>
          </div>
        );

      case 'error':
        return (
          <div className="text-center py-6">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <p className="text-red-600 font-medium mb-2">Erreur de génération</p>
            <p className="text-gray-500 text-sm mb-4">{arError || 'Une erreur est survenue'}</p>
            <button
              onClick={handleGenerate}
              disabled={loading}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-purple-600 to-pink-500 text-white rounded-xl hover:from-purple-700 hover:to-pink-600 disabled:opacity-50 transition-all"
            >
              {loading ? <Loader className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
              Réessayer
            </button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      {/* Toast notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <div className="border border-gray-200 rounded-2xl p-5 bg-gray-50 mt-6">
      <div className="flex items-center gap-3 mb-4 pb-4 border-b border-gray-200">
        <div className="p-2.5 bg-gradient-to-r from-purple-100 to-pink-100 rounded-xl">
          <Box className="w-6 h-6 text-purple-600" />
        </div>
        <div>
          <h3 className="font-semibold text-gray-800 text-lg">Modèle 3D / AR</h3>
          <p className="text-xs text-gray-500">Visualisation en réalité augmentée</p>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm flex items-center gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {renderContent()}
    </div>
    </>
  );
};

export default ProductARSection;
